// Operations -> Active Jobs view helpers (George 2026-09-28: "need to be able
// to search all headers"). Pure ESM, no React: the four Active Jobs tables
// (pages/Operations.jsx ActiveSection) read activeJobsColumns() for the
// search box and sort under every header, and server/test/activeJobsView.test.js
// imports this file.
//
// A header's search matches the text its cell SHOWS, so anything you can read
// in a column you can find by typing it there: both Date boxes as MM/DD/YYYY,
// "8 days", the crew plan's crafts and "need N", "RM · 3 inv", "75% win",
// "Rescue". A blank cell matches nothing. Money matches the printed figure
// ($1,500,000) and the bare digits (1500000). Sorting reads the underlying
// value, not the text: dates chronologically, money and days as numbers,
// blanks last either way.

// Stored format YYYY-MM-DD -> the MM/DD/YYYY the US date boxes show.
export const isoToUs = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

// Start/End <-> duration math. End follows the app's own convention:
// end = start + duration_days (matches projectStatuses.js auto-complete and
// CrewWindowCell), so duration = end - start. The Date cell's End box and the
// Date search read this one function, so they cannot disagree.
export const addDaysIso = (iso, days) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// The job's Start and End as the Date cell derives them: Start is the first
// ten characters of date_planned, End only when there is a Start AND a
// duration.
export function jobWindow(p) {
  const m = /^\d{4}-\d{2}-\d{2}/.exec(String(p && p.date_planned != null ? p.date_planned : ''));
  const start = m ? m[0] : '';
  const days = parseInt(p && p.duration_days, 10);
  return { start, end: start && days ? addDaysIso(start, days) : '' };
}

// Crew plan totals (server: labor_plan, job_craft_requirements vs
// job_allocations). short is per craft, not total minus total: 7 assigned
// against 5 PM + 2 Tech can still be 2 techs short. CrewPlanBlock prints
// these numbers and the Equipment / Labor search reads the same ones.
export function crewPlanTotals(plan) {
  const rows = Array.isArray(plan) ? plan : [];
  const num = (v) => Number(v) || 0;
  return {
    rows,
    needed: rows.reduce((s, r) => s + num(r.needed), 0),
    assigned: rows.reduce((s, r) => s + num(r.assigned), 0),
    short: rows.reduce((s, r) => s + Math.max(0, num(r.needed) - num(r.assigned)), 0),
  };
}

// What CrewPlanBlock prints under the equipment summary. "No crew plan" only
// where a plan is expected (Won / Active); a bid with no plan prints nothing.
export function crewPlanText(plan, status) {
  const { rows, needed, assigned, short } = crewPlanTotals(plan);
  if (!rows.length) return (status === 'Won' || status === 'Active') ? 'No crew plan' : '';
  return `${assigned}/${needed}${short > 0 ? ` · need ${short}` : ' ✓'} `
    + rows.map(r => `${r.needed}× ${r.craft}`).join(' · ');
}

// ActiveFiltersBar chip names, one per searchable column.
export const ACTIVE_JOBS_COL_LABELS = {
  date: 'Date', site: 'Site', job: 'Job #', duration: 'Duration', equipment: 'Equipment / Labor',
  po: 'PO #', est: 'Est. $', actual: 'Actual $', branch: 'Branch', am: 'Acct Mgr', status: 'Status',
  files: 'Files',
};

const joinText = (...parts) => parts.filter(Boolean).join(' ');
const wholeDigits = (v) => String(Math.round(Number(v) || 0));
const cents = (v) => Math.round((Number(v) || 0) * 100);

// Search and sort accessors for one Active Jobs table, keyed like
// ACTIVE_JOBS_COL_LABELS, for useTableControls(rows, search, sort).
//   money:      the cells' money formatter (UI.jsx fmtMoney), so the searched
//               figure is the printed one
//   rmFlags:    rm_flags parser (RmInvoicesModal.jsx rmFlagsOf), same reason
//   showWinPct: the In the Hunt table prints "75% win" under Est. $
//   rowActions: the table prints the FS push / TA-BR / hide controls in Files
//               (Active, Won, Recently Complete; In the Hunt has none)
// A column missing from `sort` sorts on its search text.
export function activeJobsColumns({ money, rmFlags, showWinPct = false, rowActions = false }) {
  const search = {
    date: p => { const w = jobWindow(p); return joinText(isoToUs(w.start), isoToUs(w.end)); },
    site: p => joinText(p.site, p.is_rescue ? 'Rescue' : ''),
    job: p => p.job_number || '',
    duration: p => (p.duration_days ? `${p.duration_days} days` : ''),
    equipment: p => joinText(p.equipment_type, crewPlanText(p.labor_plan, p.status)),
    po: p => p.po_number || '',
    est: p => joinText(
      money(p.estimated_value), wholeDigits(p.estimated_value),
      p.has_estimate === false ? 'No Estimate / Opportunity Only' : '',
      showWinPct && p.win_pct != null ? `${p.win_pct}% win` : '',
    ),
    actual: p => {
      const flags = rmFlags(p.rm_flags) || {};
      const held = Number(flags.held_out_amount) || 0;
      return joinText(
        p.actual_value != null ? `${money(p.actual_value)} ${wholeDigits(p.actual_value)}` : '',
        p.actual_source === 'rm' ? `RM · ${p.rm_invoice_count ?? 0} inv` : '',
        flags.no_invoices ? 'No RentalMan invoices yet' : '',
        held > 0 ? `${money(held)} held out (shared)` : '',
      );
    },
    branch: p => p.branch_name || '',
    am: p => p.account_owner_name || '',
    status: p => p.status || '',
  };
  // The Files cell's chips, with a word for each symbol so it can be typed:
  // "fail" finds FS push failures, "BR" the branch-billed jobs, "hidden" the
  // jobs kicked out of the ops view (visible with "Show hidden").
  if (rowActions) {
    search.files = p => joinText(
      p.fs_push_status === 'ok' ? 'FS ✓ pushed' : p.fs_push_status ? 'FS ✗ failed' : '→FS not pushed',
      p.branch_billed ? 'BR' : 'TA',
      p.ops_hidden ? 'hidden' : '',
    );
  }
  const sort = {
    date: p => jobWindow(p).start || null,
    duration: p => parseInt(p.duration_days, 10) || null,
    // The equipment summary alone: a crew plan under a blank summary must not
    // sort ahead of every named one.
    equipment: p => p.equipment_type || null,
    // The cell prints $0 for a blank estimate, so it sorts as zero.
    est: p => cents(p.estimated_value),
    // A blank Actual prints nothing and sorts last.
    actual: p => (p.actual_value != null ? cents(p.actual_value) : null),
  };
  return { search, sort };
}
